import { useState, useEffect, useMemo } from 'react';
import { Icon } from 'semantic-ui-react';
import './SharedItemModal.css';

const AUTO_CLOSE_SECONDS = 4;

/**
 * Success modal after claiming a share link.
 *
 * Handles everything from a single try-on up to a whole claimed closet, so it
 * groups looks and garments and only auto-closes small claims — a 40-item
 * bundle is worth reading before it disappears.
 */
const SharedItemModal = ({ isOpen, onClose, sharerName, items = [], title, sourceType, closet }) => {
  const [countdown, setCountdown] = useState(AUTO_CLOSE_SECONDS);

  const { looks, clothes, newCount, allAlreadyOwned } = useMemo(() => {
    const looksList = items.filter((i) => i.isLook);
    const clothesList = items.filter((i) => !i.isLook);
    return {
      looks: looksList,
      clothes: clothesList,
      newCount: items.filter((i) => !i.alreadyOwned).length,
      allAlreadyOwned: items.length > 0 && items.every((i) => i.alreadyOwned)
    };
  }, [items]);

  // Big claims stay up until dismissed — there's too much to take in at a glance
  const autoCloses = items.length <= 6;

  useEffect(() => {
    if (!isOpen || !autoCloses) return undefined;

    setCountdown(AUTO_CLOSE_SECONDS);

    const timer = setTimeout(onClose, AUTO_CLOSE_SECONDS * 1000);
    const interval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [isOpen, autoCloses, onClose]);

  if (!isOpen) return null;

  const label = sharerName ? `${sharerName}'s` : "A friend's";

  const headline = allAlreadyOwned
    ? 'Already in your closet'
    : `${label} ${title ? `“${title}”` : items.length > 1 ? 'items' : 'item'} added to your closet`;

  const renderRow = (list, heading) => {
    if (!list.length) return null;
    return (
      <div className="shared-item-group">
        <div className="shared-item-group-heading">
          {heading} <span>{list.length}</span>
        </div>
        <div className="shared-item-row">
          {list.map((item, idx) => (
            <div
              key={item.id || idx}
              className={`shared-item-thumbnail ${item.alreadyOwned ? 'owned' : ''}`}
              title={item.clothesName || ''}
            >
              {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.clothesName || 'Shared item'} />
              ) : (
                <Icon name="tshirt" size="large" className="shared-item-placeholder-icon" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="shared-item-modal-overlay" onClick={onClose}>
      <div
        className={`shared-item-modal ${items.length > 6 ? 'wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="shared-item-check"
          style={allAlreadyOwned ? { background: '#FEF3C7', color: '#D97706' } : {}}
        >
          {allAlreadyOwned ? (
            <Icon name="folder open" size="small" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>

        {renderRow(looks, 'Looks')}
        {renderRow(clothes, 'Clothing')}

        <div className="shared-item-title">{headline}</div>

        {allAlreadyOwned ? (
          <div className="shared-item-name">
            {items.length > 1 ? 'These items are' : 'This item is'} already in your closet
          </div>
        ) : (
          <div className="shared-item-name">
            {newCount} new item{newCount === 1 ? '' : 's'}
            {closet?.name ? ` in ${closet.name}` : ''}
            {items.length !== newCount ? ` · ${items.length - newCount} already yours` : ''}
          </div>
        )}

        {autoCloses ? (
          <div className="shared-item-countdown">
            <div className="shared-item-progress-track">
              <div
                className="shared-item-progress-fill"
                style={{ width: `${((AUTO_CLOSE_SECONDS - countdown) / AUTO_CLOSE_SECONDS) * 100}%` }}
              />
            </div>
            Closing in {countdown}s
          </div>
        ) : (
          <button className="shared-item-done" onClick={onClose}>
            View my closet
          </button>
        )}
      </div>
    </div>
  );
};

export default SharedItemModal;
